import { createTheme } from "@mui/material/styles";

export const defualtPrimaryColor = "#3E4094";
export const defualtPrimaryInfo = "#a9abd8";
export const secondaryColor = "#f2f2f2";
export const black = "#1E1E1E";
export const secondaryBlack = "#bebebe";
export const defaultBgLightWhite = "#FFFFFF";
export const defaultBgSuccess = "#219721";
export const defaultBgError = "#bd3030";
export const defaultBgLightError = "#df7171";
export const successPaymant = "#219721";
export const failurePaymant = "#bd3030";
export const cancelPaymant = "#bf3636";
export const panddingPaymant = "#c3c369";

// Mobile app aligned colors
export const mobileAppPrimary = "#950904";
export const mobileAppAccent = "#F58634";
export const mobileAppBlue = "#3E4095";
export const mobileAppBackground = "#F5F7F9";
export const mobileAppPrimaryText = "#1E2538";
export const mobileAppSecondaryText = "#909090";
export const mobileAppSuccess = "#23AF6F";
export const mobileAppError = "#FF3939";
export const mobileAppWarning = "#FFB800";

// Breakpoints
const breakpoints = {
    values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
    },
};

export const lightTheme = createTheme({
    breakpoints,
    palette: {
        mode: "light",
        primary: {
            main: defualtPrimaryColor,
        },
        info: {
            main: defualtPrimaryInfo,
        },
        secondary: {
            main: secondaryColor,
        },
        error: {
            main: defaultBgError,
            light: defaultBgLightError,
        },
        success: {
            main: defaultBgSuccess,
        },
        warning: {
            main: mobileAppWarning,
        },
        bgBlack: {
            main: black,
        },
        bgLightBlack: {
            main: secondaryBlack,
        },
        bgLightWhite: {
            main: defaultBgLightWhite,
        },
        successPaymant: {
            main: successPaymant
        },
        failurePaymant: {
            main: failurePaymant
        },
        cancelPaymant: {
            main: cancelPaymant
        },
        panddingPaymant: {
            main: panddingPaymant
        },
        // Mobile app aligned colors
        mobileApp: {
            primary: mobileAppPrimary,
            accent: mobileAppAccent,
            blue: mobileAppBlue,
            background: mobileAppBackground,
            primaryText: mobileAppPrimaryText,
            secondaryText: mobileAppSecondaryText,
            success: mobileAppSuccess,
            error: mobileAppError,
        },
        background: {
            default: "#FFFFFF",
            paper: "#FFFFFF",
            light: mobileAppBackground,
        },
    },
    typography: {
        fontFamily: "'Inter', sans-serif",
        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
            '@media (max-width:900px)': {
                fontSize: '2rem',
            },
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.3,
            '@media (max-width:900px)': {
                fontSize: '1.75rem',
            },
            '@media (max-width:600px)': {
                fontSize: '1.25rem',
            },
        },
        h3: {
            fontSize: '1.75rem',
            fontWeight: 600,
            lineHeight: 1.3,
            '@media (max-width:900px)': {
                fontSize: '1.5rem',
            },
            '@media (max-width:600px)': {
                fontSize: '1.125rem',
            },
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
            '@media (max-width:900px)': {
                fontSize: '1.25rem',
            },
            '@media (max-width:600px)': {
                fontSize: '1rem',
            },
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
            '@media (max-width:600px)': {
                fontSize: '1rem',
            },
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.5,
            '@media (max-width:600px)': {
                fontSize: '0.875rem',
            },
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.5,
            '@media (max-width:600px)': {
                fontSize: '0.875rem',
            },
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
            '@media (max-width:600px)': {
                fontSize: '0.8125rem',
            },
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.5,
            '@media (max-width:600px)': {
                fontSize: '0.875rem',
            },
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.5,
            '@media (max-width:600px)': {
                fontSize: '0.8125rem',
            },
        },
        caption: {
            fontSize: '0.75rem',
            lineHeight: 1.5,
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
        },
        button: {
            textTransform: "none",
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 8,
    },
    spacing: 8,
    components: {
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    '@media (hover: none) and (pointer: coarse)': {
                        '&:active': {
                            transform: 'scale(0.98)',
                        },
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    padding: '10px 20px',
                    minHeight: 44,
                    '@media (max-width:600px)': {
                        padding: '10px 16px',
                        minHeight: 44,
                        fontSize: '0.875rem',
                    },
                },
                sizeSmall: {
                    padding: '8px 12px',
                    minHeight: 40,
                    '@media (max-width:600px)': {
                        minHeight: 40,
                    },
                },
                sizeLarge: {
                    padding: '12px 24px',
                    minHeight: 48,
                    '@media (max-width:600px)': {
                        padding: '10px 20px',
                        minHeight: 44,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    minWidth: 44,
                    minHeight: 44,
                    '@media (max-width:600px)': {
                        minWidth: 44,
                        minHeight: 44,
                    },
                },
                sizeSmall: {
                    minWidth: 40,
                    minHeight: 40,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiInputBase-root': {
                        minHeight: 44,
                        '@media (max-width:600px)': {
                            minHeight: 44,
                        },
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    '@media (max-width:600px)': {
                        fontSize: '0.875rem',
                    },
                },
                input: {
                    padding: '12px 14px',
                    '@media (max-width:600px)': {
                        padding: '10px 12px',
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        fontSize: '0.875rem',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '@media (max-width:600px)': {
                        borderRadius: 10,
                    },
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: 20,
                    '&:last-child': {
                        paddingBottom: 20,
                    },
                    '@media (max-width:600px)': {
                        padding: 16,
                        '&:last-child': {
                            paddingBottom: 16,
                        },
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    '@media (max-width:600px)': {
                        margin: 16,
                        width: 'calc(100% - 32px)',
                        maxHeight: 'calc(100% - 32px)',
                        borderRadius: 12,
                    },
                },
                paperFullScreen: {
                    borderRadius: 0,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: '20px 24px',
                    '@media (max-width:600px)': {
                        padding: '16px 20px',
                        fontSize: '1.125rem',
                    },
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '20px 24px',
                    '@media (max-width:600px)': {
                        padding: '16px 20px',
                    },
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '16px 24px',
                    '@media (max-width:600px)': {
                        padding: '12px 20px',
                        flexDirection: 'column',
                        '& > :not(:first-of-type)': {
                            marginLeft: 0,
                            marginTop: 8,
                        },
                        '& .MuiButton-root': {
                            width: '100%',
                        },
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '12px 16px',
                    '@media (max-width:600px)': {
                        padding: '8px 12px',
                        fontSize: '0.8125rem',
                    },
                },
                head: {
                    fontWeight: 600,
                    backgroundColor: '#F5F7F9',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child td': {
                        borderBottom: 0,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    '@media (max-width:600px)': {
                        height: 28,
                        fontSize: '0.75rem',
                    },
                },
                sizeSmall: {
                    height: 24,
                    '@media (max-width:600px)': {
                        height: 22,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: 48,
                    padding: '12px 16px',
                    '@media (max-width:600px)': {
                        minHeight: 44,
                        padding: '10px 12px',
                        fontSize: '0.8125rem',
                        minWidth: 'auto',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        minHeight: 40,
                    },
                },
                scrollButtons: {
                    '@media (max-width:600px)': {
                        width: 44,
                        minWidth: 44,
                    },
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    '@media (max-width:600px)': {
                        width: '85%',
                        maxWidth: 300,
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        padding: '0 8px',
                    },
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        minHeight: 56,
                        padding: '0 8px',
                    },
                },
            },
        },
        MuiPagination: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        '& .MuiPaginationItem-root': {
                            minWidth: 40,
                            height: 40,
                            fontSize: '0.8125rem',
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: 12,
                    '@media (max-width:600px)': {
                        borderRadius: 10,
                    },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        padding: '4px 0',
                    },
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        paddingTop: 8,
                        paddingBottom: 8,
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    '@media (max-width:600px)': {
                        minHeight: 44,
                        paddingTop: 10,
                        paddingBottom: 10,
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    minHeight: 44,
                    '@media (max-width:600px)': {
                        minHeight: 44,
                        fontSize: '0.875rem',
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    minHeight: '1.4375em',
                    '@media (max-width:600px)': {
                        fontSize: '0.875rem',
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        width: '100%',
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        fontSize: '0.75rem',
                        marginLeft: 8,
                    },
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        fontSize: '0.8125rem',
                    },
                },
                separator: {
                    '@media (max-width:600px)': {
                        marginLeft: 4,
                        marginRight: 4,
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    '@media (max-width:600px)': {
                        padding: '8px 12px',
                        fontSize: '0.8125rem',
                    },
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        left: 16,
                        right: 16,
                        bottom: 16,
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '0.75rem',
                    borderRadius: 6,
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        width: 36,
                        height: 36,
                        fontSize: '0.875rem',
                    },
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    '@media (max-width:600px)': {
                        fontSize: '0.625rem',
                        minWidth: 16,
                        height: 16,
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    '&:before': {
                        display: 'none',
                    },
                    '@media (max-width:600px)': {
                        borderRadius: 8,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    minHeight: 56,
                    '@media (max-width:600px)': {
                        minHeight: 48,
                        padding: '0 12px',
                    },
                },
                content: {
                    '@media (max-width:600px)': {
                        margin: '10px 0',
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        padding: '8px 12px 16px',
                    },
                },
            },
        },
        MuiStepper: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        padding: 0,
                    },
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: {
                    '@media (max-width:600px)': {
                        fontSize: '0.75rem',
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        width: 42,
                        height: 26,
                        padding: 0,
                    },
                },
            },
        },
        MuiFab: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        width: 48,
                        height: 48,
                    },
                },
                sizeMedium: {
                    '@media (max-width:600px)': {
                        width: 44,
                        height: 44,
                    },
                },
            },
        },
        MuiSpeedDial: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        position: 'fixed',
                        bottom: 'calc(50px + env(safe-area-inset-bottom) + 8px)',
                        right: 16,
                    },
                },
            },
        },
    },
});